import { useState, useCallback, useRef, useEffect } from "react";

/**
 * useApi Hook
 * Standardizes loading, error, and data states for async operations.
 * 
 * @param {Function} apiFunc - The async function to execute.
 * @param {Boolean} initialLoading - Whether the hook should start in a loading state.
 * @returns {Object} { data, isLoading, isError, error, execute }
 */
const useApi = (apiFunc, initialLoading = false) => {
  const [state, setState] = useState({
    data: null,
    isLoading: initialLoading,
    isError: false,
    error: null,
  });

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const execute = useCallback(
    async (...args) => {
      if (isMounted.current) {
        setState((prev) => ({ ...prev, isLoading: true, isError: false, error: null }));
      }

      try {
        const data = await apiFunc(...args);

        if (isMounted.current) {
          setState({
            data,
            isLoading: false,
            isError: false,
            error: null,
          });
        }

        return data;
      } catch (error) {
        if (isMounted.current) {
          setState({
            data: null,
            isLoading: false,
            isError: true,
            error: error.message || "Ocurrió un error inesperado",
          });
        }

        throw error;
      }
    },
    [apiFunc]
  );

  return { ...state, execute };
};

export default useApi;
